import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { faAddressBook, faCircleCheck, faClock } from "@fortawesome/free-solid-svg-icons"
import List from "../../../components/List/List"
import { AdminLandingLead } from "../../../Objects/LandingLead/AdminLandingLead"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { MetricCard } from "../../../components/MetricCard/MetricCard"
import { LandingLeadDetailsPopup } from "../LandingLeadDetailsPopup"
import { AdminListCard, AdminListMetricItem } from "../components/AdminListCard"

interface Props {
  leads: AdminLandingLead[]
  onToggle: (id: number, value: boolean) => void
  onRefresh: () => void
}

const LEAD_FILTERS = ["filter_open", "filter_closed", "filter_all"] as const

const dateOnly = (value: string) => value.split("T")[0]

export const LandingLeadsSection: React.FC<Props> = ({ leads, onToggle, onRefresh }) => {
  const { t } = useTranslation("adminLandingLeads")
  const [selectedLead, setSelectedLead] = useState<AdminLandingLead | null>(null)
  const openContacts = leads.filter((lead) => !lead.hasBeenContacted).length
  const emptyValue = t("empty_value")
  const dropdownFilters = LEAD_FILTERS.map((key) => t(key))

  return (
    <div className="h-100vh w-full overflow-auto bg-white p-1 md:p-0">
      {selectedLead && <LandingLeadDetailsPopup lead={selectedLead} onClose={() => setSelectedLead(null)} />}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2 p-1">
        <MetricCard title={t("metrics.total")} value={`${leads.length}`} icon={faAddressBook} />

        <MetricCard title={t("metrics.open")} value={`${openContacts}`} icon={faClock} />

        <MetricCard title={t("metrics.closed")} value={`${leads.length - openContacts}`} icon={faCircleCheck} />
      </div>

      <div className="mt-2 p-2">
        <List
          messageCount={t("count")}
          messageEmpty={t("empty")}
          nameFindSearch={t("search")}
          nameDropdown={dropdownFilters}
          data={leads.map((lead, index) => {
            const status = lead.hasBeenContacted ? t("status_closed") : t("status_open")
            const title = lead.name || lead.businessName || emptyValue

            return {
              id: lead.id,
              index,
              title,
              linkImage: "",
              isDisable: lead.hasBeenContacted,
              dataFilter: [
                lead.name ?? "",
                lead.businessName ?? "",
                lead.email ?? "",
                lead.phoneNumber ?? "",
                lead.trafficSource ?? "",
                lead.formName ?? "",
                status,
                dateOnly(lead.createdAt),
                ...lead.answers.flatMap((answer) => [answer.label, answer.value]),
              ],
              children: (
                <AdminListCard
                  title={title}
                  subtitle={lead.businessName || lead.email || lead.phoneNumber || emptyValue}
                  badge={status}
                  badgeClass={lead.hasBeenContacted ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}
                  cardClass={
                    lead.hasBeenContacted
                      ? "rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3"
                      : "rounded-xl border-2 border-amber-200 bg-amber-50/40 p-3"
                  }
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <AdminListMetricItem label={t("fields.phone")} value={lead.phoneNumber || emptyValue} />

                    <AdminListMetricItem label={t("fields.email")} value={lead.email || emptyValue} />

                    <AdminListMetricItem label={t("fields.source")} value={lead.trafficSource || emptyValue} />

                    <AdminListMetricItem label={t("fields.form")} value={lead.formName || emptyValue} />
                  </div>

                  <div
                    onClick={(event) => event.stopPropagation()}
                    className="rounded-lg border border-orange-100 bg-white p-2"
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.landingLeadsSection.button.eventEventStopPropagation"
                  >
                    <SimpleToggle
                      title={t("contacted")}
                      value={lead.hasBeenContacted}
                      onChange={(value) => onToggle(lead.id, value)}
                      descriptionKey="interactionDescriptions:pages.adminDashboard.sections.landingLeadsSection.toggle.contacted"
                    />
                  </div>
                </AdminListCard>
              ),
            }
          })}
          handleRefreshButton={onRefresh}
          handleOnClick={(id) => {
            const lead = leads.find((item) => item.id === id)

            if (lead) setSelectedLead(lead)
          }}
        />
      </div>
    </div>
  )
}
